/**
 * Inventory Dashboard Page
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Package, Euro, AlertTriangle, Bell, Plus, ArrowRight, Box, FileText, History } from 'lucide-react';
import { inventoryApi } from '../lib/api/inventory';
import type { InventoryDashboard } from '../types/inventory';
import { InventoryAlertCard } from '../components/inventory/InventoryAlertCard';
import { IngredientCard } from '../components/inventory/IngredientCard';

export default function InventoryDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<InventoryDashboard | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const data = await inventoryApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load inventory dashboard:', error);
      alert('Errore nel caricamento della dashboard inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await inventoryApi.resolveAlert(alertId);
      loadDashboard(); // Reload to update alerts
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      alert('Errore nella risoluzione dell\'alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mb-3"></div>
          <p className="text-gray-500 dark:text-white/40">Caricamento Dashboard Inventario...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-gray-400 dark:text-white/30" />
          </div>
          <p className="text-gray-500 dark:text-white/40">Nessun dato disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Gestione Inventario</h1>
          <p className="text-gray-500 dark:text-white/40 text-sm mt-1">Monitora scorte, costi e movimenti magazzino</p>
        </div>
        <Link
          to="/dashboard/inventory/ingredients"
          className="px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center gap-2 font-medium"
        >
          <Package size={18} />
          Gestisci Ingredienti
        </Link>
      </div>

      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                <Package size={20} className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{dashboard.totalIngredients}</div>
                <div className="text-sm text-gray-500 dark:text-white/40">Ingredienti totali</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-400 dark:text-white/30">{dashboard.activeIngredients} attivi</div>
          </div>

          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-emerald-200 dark:border-emerald-500/20 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                <Euro size={20} className="text-emerald-500 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{dashboard.totalInventoryValue.toFixed(2)}</div>
                <div className="text-sm text-gray-500 dark:text-white/40">Valore inventario</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-amber-200 dark:border-amber-500/20 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-500 dark:text-amber-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{dashboard.lowStockCount}</div>
                <div className="text-sm text-gray-500 dark:text-white/40">Scorte basse</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-red-200 dark:border-red-500/20 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                <Bell size={20} className="text-red-500 dark:text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{dashboard.criticalAlertsCount}</div>
                <div className="text-sm text-gray-500 dark:text-white/40">Alert critici</div>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts Section */}
        {dashboard.recentAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Alert Attivi</h2>
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                {dashboard.recentAlerts.length} alert
              </span>
            </div>
            <div className="space-y-3">
              {dashboard.recentAlerts.map((alert) => (
                <InventoryAlertCard
                  key={alert.id}
                  alert={alert}
                  onResolve={handleResolveAlert}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Critical Stock Ingredients */}
        {dashboard.criticalStockIngredients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-[#25252d] rounded-2xl border border-red-200 dark:border-red-500/20 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-500 dark:text-red-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Scorte Critiche</h2>
              </div>
              <Link
                to="/dashboard/inventory/ingredients"
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium flex items-center gap-1"
              >
                Vedi tutti <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard.criticalStockIngredients.slice(0, 6).map((ingredient) => (
                <IngredientCard key={ingredient.id} ingredient={ingredient} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Low Stock Ingredients */}
        {dashboard.lowStockIngredients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-[#25252d] rounded-2xl border border-amber-200 dark:border-amber-500/20 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-amber-500 dark:text-amber-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Scorte Basse</h2>
              </div>
              <Link
                to="/dashboard/inventory/ingredients"
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium flex items-center gap-1"
              >
                Vedi tutti <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard.lowStockIngredients.slice(0, 6).map((ingredient) => (
                <IngredientCard key={ingredient.id} ingredient={ingredient} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Azioni Rapide</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/dashboard/inventory/ingredients/new"
              className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-orange-200 dark:hover:border-orange-500/20 transition-all group"
            >
              <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                <Plus size={20} className="text-orange-500 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">Nuovo Ingrediente</h3>
                <p className="text-xs text-gray-500 dark:text-white/40">Aggiungi al magazzino</p>
              </div>
            </Link>
            <Link
              to="/dashboard/inventory/ingredients"
              className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all group"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                <Box size={20} className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Gestisci Ingredienti</h3>
                <p className="text-xs text-gray-500 dark:text-white/40">Vedi lista completa</p>
              </div>
            </Link>
            <Link
              to="/dashboard/inventory/purchase"
              className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-emerald-200 dark:hover:border-emerald-500/20 transition-all group"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                <FileText size={20} className="text-emerald-500 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">Registra Acquisto</h3>
                <p className="text-xs text-gray-500 dark:text-white/40">Carica merce in magazzino</p>
              </div>
            </Link>
            <Link
              to="/dashboard/inventory/movements"
              className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-100 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-violet-200 dark:hover:border-violet-500/20 transition-all group"
            >
              <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center">
                <History size={20} className="text-violet-500 dark:text-violet-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">Storico Movimenti</h3>
                <p className="text-xs text-gray-500 dark:text-white/40">Vedi cronologia</p>
              </div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
